import { Box, Button, Card, CardContent, Chip, Container, Link, Typography } from "@mui/material";
import { FC, UIEvent, useEffect, useMemo, useRef, useState } from "react";

import { Download, LaunchOutlined } from "@mui/icons-material";
import { isEmpty } from "lodash";
import MaterialReactTable from "material-react-table";
import { useHistory } from "react-router-dom";
import { getConfigs } from "../../configs";
import { API_SLUGS, ROUTES } from "../../constants";
import { useTableInfinity } from "../../hooks/process/useTableInfinity";
import { convertDurationIntoHoursMinutesSeconds, displayDateTimeWithTimezone } from "../../utils/conversions";
import { downloadCSV } from "../../utils/downlodCSV";
import { Labels } from "../analysis/ViewFindings/runComponents/labels";
import Metrics from "../common/metrics";
import TruncatedScrollingText from "../common/truncatedScrollingText";

const limit = 30;
const disableInfiniteScroll = false;

const AssetReportTable: FC<any> = ({ groupId, projectId }) => {
    const path = API_SLUGS.RUNS_REPORT + `?limit={limit}&offset={offset}`;
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const [columnHeaderFilter, setColumnHeaderFilter] = useState<any>({});
    const [isDownloading, setIsDownloading] = useState(false);
    const assetReportUrl = useMemo(() => {
        let newPath = path
        if (!isEmpty(columnHeaderFilter)) {
            for (let key in columnHeaderFilter) {
                const columnFilterValues = columnHeaderFilter[key];
                for (let value of columnFilterValues) {
                    if (value !== null) {
                        newPath += `&${key}=${value}`;
                    }
                }
            }
        }
        return newPath;
    }
        , [columnHeaderFilter, path])
    const { rows, fetchMoreOnBottomReached, isFetching } = useTableInfinity(
        "runsReport",
        assetReportUrl,
        tableContainerRef,
        limit
    );


    const downloadCSVReport = async () => {
        setIsDownloading(true);
        await downloadCSV(`${getConfigs().baseApiUrl}${API_SLUGS.RUNS_REPORT}?downloadCsv=true`, `aptori_asset_run_report`)
        setIsDownloading(false);
    };

    useEffect(() => {
        if (!disableInfiniteScroll) {
            fetchMoreOnBottomReached(tableContainerRef.current);
        }
    }, [fetchMoreOnBottomReached])

    const navigate = useHistory();
    return (
        <Box
            sx={{
                backgroundColor: "background.paper",
                minHeight: '100%',
            }}
        >
            <Container maxWidth={false} sx={{ p: 3, pt: 1.5 }}>
                <Box>
                    <Box sx={{ display: "flex", justifyContent: "end", pb: 2 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => !isDownloading && downloadCSVReport()}
                            sx={{ color: "text.primary", borderColor: "neutral.300" }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "start" }}>
                                <Download fontSize="small" />
                                Download CSV Report

                            </Box>
                        </Button>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <MaterialReactTable muiTablePaperProps={{
                            elevation: 0
                        }} enableGlobalFilter={false}
                            columns={[
                                {
                                    accessorKey: "name",
                                    header: "Asset",

                                    size: 120,
                                    Cell: (params: any) => {
                                        const path = ROUTES.PROJECT.replace(':projectId', params?.row?.original?.projectId) + `?assetid=${params?.row?.original?.id}`
                                        return (
                                            <Box style={{ fontWeight: "bold", display: "flex" }} className="hand">
                                                <Link><TruncatedScrollingText typographyVariant="h6" onClick={() => navigate.push(path)} name={params?.row?.original?.name} maxLength={30} labels={params?.row?.original?.labels} /></Link>
                                            </Box>
                                        )
                                    },
                                },
                                {
                                    accessorKey: "Group Name",
                                    header: "Group",

                                    size: 120,
                                    Cell: (params: any) => {
                                        return (
                                            <TruncatedScrollingText typographyVariant="body2" name={params?.row?.original?.groupName} maxLength={30} labels={params?.row?.original?.labels} />

                                        )
                                    },
                                },
                                {
                                    accessorKey: "projectName",
                                    header: "Project",

                                    size: 100,
                                    Cell: (params: any) => (
                                        <TruncatedScrollingText typographyVariant='body2' name={params?.row?.original?.projectName} labels={params?.row?.original?.projectLabels} maxLength={15} />
                                    )
                                },
                                {
                                    accessorKey: "environmentName",
                                    header: "Environment",

                                    size: 100,
                                },
                                {
                                    accessorKey: "type",
                                    header: "Type",

                                    size: 50,
                                    Cell: (params: any) => (<>
                                        {params?.row?.original?.latestRunType && (<Chip
                                            label={params?.row?.original?.latestRunType.toUpperCase()}
                                            size="small"
                                            variant={"filled"}
                                            color="default"
                                        />
                                        )}
                                    </>)

                                },
                                {
                                    accessorKey: "metrics",
                                    header: "Open Issues By Risk",

                                    Cell: (params: any) => {
                                        return (
                                            <Metrics data={params?.row?.original?.incidentsCountByRisk} />
                                        )
                                    }
                                },
                                {
                                    accessorKey: "latestRunId",
                                    header: "Latest Run Id",
                                    Cell: (params: any) => {
                                        const path = ROUTES.FINDINGS + `?resultId=${params?.row?.original?.latestRunId}`
                                        return (
                                            <Box style={{ fontWeight: "bold", display: "flex" }} className="hand">
                                                {params?.row?.original?.latestRunId ?
                                                    (
                                                        <Link>
                                                            <Typography variant="h6" color='primary' onClick={() => navigate.push(path)}>
                                                                {params?.row?.original?.latestRunId?.split('-')?.[0]}... <LaunchOutlined fontSize="inherit" />
                                                            </Typography>
                                                        </Link>
                                                    ) : <></>}
                                            </Box>
                                        )
                                    },

                                    size: 110,
                                },
                                {
                                    accessorKey: "latestRunTimestamp",
                                    header: "Latest Run Timestamp",
                                    Cell: (params: any) => (<>
                                        {displayDateTimeWithTimezone(params?.row?.original?.latestRunTimestamp) || "No run initiated"}
                                    </>),

                                    size: 150,
                                },
                                {
                                    accessorKey: "latestRunStatus",
                                    header: "Latest Run Status",

                                    size: 100,
                                },
                                {
                                    accessorKey: "latestRunDuration",
                                    header: "Latest Run Duration",
                                    Cell: (params: any) => (<>
                                        {convertDurationIntoHoursMinutesSeconds(params?.row?.original?.latestRunDuration)}
                                    </>),

                                    size: 100,
                                },
                                {
                                    accessorKey: "latestRunTests",
                                    header: "Checks",

                                    size: 60,
                                },
                                {
                                    accessorKey: "latestRunLabels",
                                    header: "Latest Run Labels",
                                    Cell: (params: any) => {
                                        return (<>
                                            <Labels labels={params.row?.original?.latestRunLabels} />
                                            {params.row?.newIssues > 0 ? (
                                                <Chip size="small" color="success" label="New"></Chip>
                                            ) : null}
                                        </>)
                                    },

                                    size: 60,
                                },
                                {
                                    accessorKey: "latestRunAuthConfigured",
                                    header: "Latest Run Authorization",
                                    Cell: (params: any) => {
                                        return (<>
                                            {params?.row?.original?.latestRunAuthConfigured ? "Configured" : ""}
                                        </>)
                                    },

                                },
                                {
                                    accessorKey: "operationsTotal",
                                    header: "Latest Run Total Operations",
                                },
                                {
                                    accessorKey: "operationsSuccessful",
                                    header: "Latest Run Successful Operations",
                                },
                                {
                                    accessorKey: "latestRunIncidentCount",
                                    header: "Latest Run Findings by Severity",

                                    Cell: (params: any) => {
                                        return (
                                            <Metrics data={params?.row?.original?.latestRunIncidentCount} />
                                        )
                                    }
                                },

                            ]}
                            data={rows}
                            initialState={{
                                density: "compact",
                                columnPinning: { left: ['name'] }
                            }}
                            enableColumnActions={false}
                            enableSorting={false}
                            enableStickyHeader


                            enableTopToolbar={false}

                            enablePagination={false}
                            enableDensityToggle={false}
                            enableColumnFilters={false}
                            // enableRowVirtualization
                            state={{
                                showProgressBars: isFetching,
                            }}
                            muiTableContainerProps={{
                                ref: tableContainerRef, //get access to the table container element
                                sx: { maxHeight: "calc(100vh - 350px)" }, //give the table a max height
                                onScroll: (
                                    event: UIEvent<HTMLDivElement> //add an event listener to the table container element
                                ) => {
                                    if (!disableInfiniteScroll) {
                                        fetchMoreOnBottomReached(event.target as HTMLDivElement)
                                    }

                                }
                            }}
                        />
                    </Box>

                </Box>

            </Container>
        </Box>

    );
};

export default AssetReportTable;
